import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Database, Users, FileText, ArrowLeft } from 'lucide-react';
import Footer from '../../components/landing/Footer';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const sections = [
    {
      title: "Information We Collect",
      icon: Database,
      content: [
        "Personal information (name, email, phone number, address)",
        "Travel information (destination, dates, trip details)",
        "Payment information (processed securely through third-party providers)",
        "Device information (IP address, browser type, device identifiers)",
        "Usage data (how you interact with our services)"
      ]
    },
    {
      title: "How We Use Your Information",
      icon: Eye,
      content: [
        "Provide and improve our travel safety services",
        "Process insurance claims and payments",
        "Send important updates about your policies",
        "Provide customer support and assistance",
        "Comply with legal and regulatory requirements"
      ]
    },
    {
      title: "Information Sharing",
      icon: Users,
      content: [
        "We do not sell your personal information to third parties",
        "We may share information with trusted service providers",
        "Information may be shared for legal compliance purposes",
        "We may share aggregated, anonymized data for research",
        "Your information is protected by strict confidentiality agreements"
      ]
    },
    {
      title: "Data Security",
      icon: Lock,
      content: [
        "We use industry-standard encryption to protect your data",
        "Regular security audits and updates",
        "Secure data storage and transmission",
        "Limited access to personal information",
        "Regular staff training on data protection"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      {/* Back Button */}
      <div className="fixed top-24 left-4 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Back</span>
        </motion.button>
      </div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-600/10 to-emerald-600/10" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl bg-gray-300/20" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-3xl bg-emerald-300/20" />
        
        <div className="relative z-10 px-4 mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 mb-6">
              <Shield className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Privacy Policy</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl mb-6">
              Privacy Policy
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gray-600 to-emerald-600">
                Your Privacy Matters
              </span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              At TripSecure, we are committed to protecting your privacy and ensuring the security of your personal information. This policy explains how we collect, use, and safeguard your data.
            </p>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 border border-emerald-200">
              <span className="text-sm font-medium text-emerald-700">Last Updated: December 2024</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 px-4 mx-auto max-w-6xl sm:px-6">
        <div className="space-y-16">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              
              <div className="space-y-4">
                {section.content.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}

          {/* Additional Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl p-8 text-white"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-4">Your Rights</h2>
              <p className="text-lg opacity-90">
                You have the right to access, update, or delete your personal information. Contact us if you have any questions about your data.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Access Your Data</h3>
                <p className="text-sm opacity-90">Request a copy of your personal information</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">Data Protection</h3>
                <p className="text-sm opacity-90">Your information is protected by industry standards</p>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About This Policy?</h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about our privacy practices or this policy, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {/* <a
                href="mailto:privacy@tripsecuregh.com"
                className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
              >
                Contact Privacy Team
              </a> */}
              <a
                href="mailto:info@tripsecuregh.com"
                className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                General Inquiries
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
